/**
 * Mensagens entre a página da aula e o Player da Aprova Total Video Platform,
 * que roda num iframe de outra origem. O lado do Player mora no repositório
 * aprova-video-platform (infra/aws/player-transcription-addon.js, documentado
 * em docs/player-ponte-pagina.md).
 *
 * Player → página:
 *   - `aprova-total:playback-telemetry`: retrato da reprodução a cada ~5 s com
 *     o vídeo andando e logo após pausa, salto e fim;
 *   - `aprova-total:player-event`: ready, play, pause, seeked, ended, error e
 *     state (faixa "Carregando…"/"Vídeo indisponível" do Player).
 *
 * Página → Player (`aprova-total:player-command`): play, pause, state,
 * seek { seconds } e key { key, shiftKey }.
 */

export const PLAYER_TELEMETRY = 'aprova-total:playback-telemetry';
export const PLAYER_EVENT = 'aprova-total:player-event';
export const PLAYER_COMMAND = 'aprova-total:player-command';

/** Corte que considera a aula assistida — o mesmo dos players antigos. */
export const DEFAULT_VIRTUAL_FINISH_AT = 80;

export type PlayerSnapshot = {
  videoId: string;
  /** Posição no vídeo, em segundos (como o react-player do Vimeo informava). */
  playedSeconds: number;
  currentTime: number;
  duration: number;
  /** Posição em 0–100; null enquanto a duração não é conhecida. */
  percent: number | null;
  /** Só o que foi reproduzido de fato: rever não soma, pular não conta. */
  watchedSeconds: number;
  watchedPercent: number | null;
  paused: boolean;
  ended: boolean;
  playbackRate: number;
  volume: number;
  muted: boolean;
  resolution: { width: number; height: number } | null;
};

export type PlayerEventName =
  | 'ready'
  | 'play'
  | 'pause'
  | 'seeked'
  | 'ended'
  | 'error'
  | 'state';

export type PlayerMessage =
  | { kind: 'telemetry'; snapshot: Partial<PlayerSnapshot> }
  | {
      kind: 'event';
      event: PlayerEventName;
      snapshot: Partial<PlayerSnapshot> & { hidden?: boolean; text?: string };
    };

export type PlayerCommand =
  | { command: 'play' | 'pause' | 'state' }
  | { command: 'seek'; seconds: number }
  | { command: 'key'; key: string; shiftKey: boolean };

const EVENTS: PlayerEventName[] = [
  'ready',
  'play',
  'pause',
  'seeked',
  'ended',
  'error',
  'state',
];

/** Origem do Player a partir da embedUrl; '' quando ela não é um endereço. */
export function playerOriginOf(embedUrl?: string): string {
  if (!embedUrl) return '';
  try {
    return new URL(embedUrl).origin;
  } catch {
    return '';
  }
}

/**
 * O vídeo do iframe, sem a credencial do fragmento nem a query: a mesma aula
 * recarregada (embedUrl nova a cada busca) continua sendo o mesmo vídeo.
 */
export function videoKeyOf(embedUrl?: string): string {
  if (!embedUrl) return '';
  try {
    const url = new URL(embedUrl);
    return `${url.origin}${url.pathname}`;
  } catch {
    return '';
  }
}

/** Lê uma mensagem do Player; null para qualquer outra coisa. */
export function readPlayerMessage(data: unknown): PlayerMessage | null {
  if (!data || typeof data !== 'object') return null;
  const message = data as {
    type?: unknown;
    event?: unknown;
    payload?: unknown;
  };
  if (!message.payload || typeof message.payload !== 'object') return null;
  const snapshot = message.payload as Partial<PlayerSnapshot>;

  if (message.type === PLAYER_TELEMETRY) {
    if (!Number.isFinite(Number(snapshot.playedSeconds))) return null;
    return { kind: 'telemetry', snapshot };
  }

  if (
    message.type === PLAYER_EVENT &&
    EVENTS.includes(message.event as PlayerEventName)
  ) {
    return { kind: 'event', event: message.event as PlayerEventName, snapshot };
  }

  return null;
}

/** Posição em 0–100; calcula pela duração quando o Player não mandou. */
export function percentOf(snapshot: Partial<PlayerSnapshot>): number | null {
  if (
    typeof snapshot.percent === 'number' &&
    Number.isFinite(snapshot.percent)
  ) {
    return snapshot.percent;
  }
  const duration = Number(snapshot.duration);
  const played = Number(snapshot.playedSeconds);
  if (!(duration > 0) || !Number.isFinite(played)) return null;
  return Math.min(100, (played / duration) * 100);
}

/** A reprodução já passou do corte que marca a aula como vista? */
export function reachedVirtualFinish(
  snapshot: Partial<PlayerSnapshot>,
  virtualFinishAt = DEFAULT_VIRTUAL_FINISH_AT
): boolean {
  if (snapshot.ended) return true;
  const percent = percentOf(snapshot);
  return percent !== null && percent >= virtualFinishAt;
}

/** Teclas que viram atalho do Player quando o foco está na página. */
const SHORTCUT_KEYS = new Set([
  ' ',
  'Spacebar',
  'k',
  'K',
  'j',
  'J',
  'l',
  'L',
  'm',
  'M',
  'c',
  'C',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  '<',
  '>',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
]);

type KeyboardLike = {
  key: string;
  shiftKey?: boolean;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  defaultPrevented?: boolean;
  target?: unknown;
};

/**
 * O atalho que uma tecla da página dispara no Player, ou null quando a tecla
 * é da página. Só vale com o foco "solto" (no body) ou no próprio player:
 * campo de texto, botão, link, menu e modal continuam com as teclas deles.
 * Tela cheia é pedida pela página — o navegador só a concede a quem recebeu
 * o gesto do aluno.
 */
export function shortcutFromKey(
  event: KeyboardLike,
  playerContainer?: { contains(node: Node | null): boolean } | null
): PlayerCommand | 'fullscreen' | null {
  if (
    event.defaultPrevented ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey
  ) {
    return null;
  }

  const target = event.target as (Node & { tagName?: string }) | null;
  const tag = String(target?.tagName ?? '').toUpperCase();
  const free = !target || tag === 'BODY' || tag === 'HTML';
  const insidePlayer = Boolean(target && playerContainer?.contains(target));
  if (!free && !insidePlayer) return null;

  if (event.key === 'f' || event.key === 'F') return 'fullscreen';
  if (!SHORTCUT_KEYS.has(event.key)) return null;
  return { command: 'key', key: event.key, shiftKey: Boolean(event.shiftKey) };
}
